import { Reducer } from "redux";
import { Models, Types, Enums, Components } from "@sendum/core";

const cloneDeep = require("lodash/cloneDeep");

import { TFilter } from "../types/TFilter";

import { DispatchAction } from "./DeviceSelectorStore";
import { DeviceSelectorActionTypes } from "./DeviceSelectorActions";

export interface IDeviceSelectorState {
	error?: Error;
	shipments?: Models.Shipment[];
	isFiltering?: boolean;
	sortBy?: Enums.SortBy;
	sortDirection?: Enums.SortDirection;
	countsFilter?: Components.Counts.ECountsItem;
	search?: string;
	filter?: TFilter;
	limit?: number;
	isInitialLoadingDevices?: boolean;
	isLoadingDevices?: boolean;
	devices?: Models.Device[];
	counts?: Models.Counts;
	loadDevicesQuery?: Types.TLoadDevicesQuery;
}

const initialState: IDeviceSelectorState = {
	sortBy: Enums.SortBy.UPDATED_AT,
	sortDirection: Enums.SortDirection.DESC,
	countsFilter: Components.Counts.ECountsItem.TOTAL,
	search: "",
	isInitialLoadingDevices: true,
	isLoadingDevices: false,
	devices: new Array<Models.Device>(),
	filter: {
		shipmentStatuses: [],
		deviceStatuses: [],
	},
	limit: 10,
};

const updateDeviceMQTT = (state: IDeviceSelectorState, payload: any): IDeviceSelectorState => {
	const delta: IDeviceSelectorState = {};
	if (!payload.mess.Device || !state.devices) {
		return delta;
	}
	const device: Models.Device | undefined = state.devices.find(d => d?.id === payload.mess.Device && d?.shipment?.id === payload.topic);

	if (!device?.id || !device.present) {
		return {};
	}
	device.present.update(payload.mess);
	delta.devices = [...state.devices];
	const index = delta.devices.findIndex(d => d?.id === device?.id && d?.shipment?.id === device?.shipment?.id);
	if (index !== -1) {
		delta.devices[index] = cloneDeep(device);
	}

	return delta;
};

export const DeviceSelectorReducer: Reducer<IDeviceSelectorState, DispatchAction> = (state = initialState, action) => {
	switch (action.type) {
		case DeviceSelectorActionTypes.RESET_STATE: {
			return { ...initialState };
		}
		case DeviceSelectorActionTypes.SET_SORT_BY: {
			return { ...state, ...{ sortBy: action.payload.sortBy } };
		}
		case DeviceSelectorActionTypes.SET_COUNTS_FILTER: {
			return { ...state, ...{ countsFilter: action.payload.countsFilter } };
		}
		case DeviceSelectorActionTypes.SET_SORT_DIRECTION: {
			return { ...state, ...{ sortDirection: action.payload.sortDirection } };
		}
		case DeviceSelectorActionTypes.SET_SEARCH: {
			return { ...state, ...{ search: action.payload.search } };
		}
		case DeviceSelectorActionTypes.SET_FILTER: {
			const delta: any = { filter: action.payload.filter };
			if (action.payload.filter.deviceStatuses[0] === Enums.EnvirotainerContainerStatus.ALLOCATED) {
				delta.sortBy = Enums.SortBy.DEVICE_ID;
			}

			return { ...state, ...delta };
		}
		case DeviceSelectorActionTypes.SET_LIMIT: {
			return { ...state, ...{ limit: action.payload.limit } };
		}
		case DeviceSelectorActionTypes.SET_LOAD_DEVICES_QUERY: {
			return { ...state, ...{ loadDevicesQuery: action.payload.query } };
		}
		case DeviceSelectorActionTypes.SET_LOADING_DEVICES: {
			return { ...state, ...{ isLoadingDevices: action.payload.newState } };
		}
		case DeviceSelectorActionTypes.END_INITIAL_LOADING_DEVICES: {
			return { ...state, ...{ isInitialLoadingDevices: false } };
		}
		case DeviceSelectorActionTypes.SET_FILTERING: {
			return { ...state, ...{ isFiltering: action.payload.newState } };
		}
		case DeviceSelectorActionTypes.CLEAR_DEVICES: {
			return { ...state, ...{ devices: new Array<Models.Device>(), counts: undefined } };
		}
		case DeviceSelectorActionTypes.ADD_DEVICES: {
			const ids = new Set(action.payload.devices.map((d: Models.Device) => `${d.id}#${d.shipment.id}`));
			const devices = state.devices ? [...state.devices] : [];

			return { ...state, ...{ devices: [...devices.filter(d => !ids.has(`${d.id}#${d.shipment.id}`)), ...action.payload.devices] } };
		}
		case DeviceSelectorActionTypes.SET_COUNTS: {
			return { ...state, ...{ counts: action.payload.counts } };
		}
		case DeviceSelectorActionTypes.UPDATE_DEVICE_MQTT: {
			return { ...state, ...updateDeviceMQTT(state, action.payload) };
		}
		case DeviceSelectorActionTypes.SET_ERROR: {
			return { ...state, ...{ error: action.payload.error } };
		}
		default:
			return state;
	}
};
